import type { Area } from 'react-easy-crop';

/**
 * Client-side square cropping for the company logo.
 *
 * The result is stored inline in the company row as a data URL, so it travels
 * in the same JSON body as the rest of the profile. Express caps request bodies
 * at its 100 KB default, so the encoder below backs off on quality (and, as a
 * last resort, on resolution) until the string fits the budget.
 */

/** Stored edge length. 256px covers the 32px chrome tile at 4x density. */
const OUTPUT_SIZE = 256;

/** Upper bound for the encoded string, leaving room for the other fields. */
export const MAX_LOGO_DATA_URL_CHARS = 60_000;

/** Rejected before decoding, so a huge file never reaches the canvas. */
export const MAX_SOURCE_BYTES = 8 * 1024 * 1024;

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Fișierul nu a putut fi citit.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Imaginea nu a putut fi încărcată.'));
    image.src = src;
  });
}

/**
 * Render the selected crop box to a square canvas and encode it.
 *
 * WebP keeps flat-colour logos small and preserves transparency; browsers that
 * cannot encode it (older Safari) silently produce PNG instead, which still
 * works — it is only larger, which the size backoff then absorbs.
 */
export async function cropToSquareDataUrl(src: string, area: Area): Promise<string> {
  const image = await loadImage(src);

  function encode(size: number, quality: number): string {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas indisponibil în acest browser.');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, size, size);
    return canvas.toDataURL('image/webp', quality);
  }

  for (const [size, quality] of [
    [OUTPUT_SIZE, 0.9],
    [OUTPUT_SIZE, 0.75],
    [OUTPUT_SIZE, 0.6],
    [128, 0.75],
    [96, 0.7],
  ] as const) {
    const url = encode(size, quality);
    if (url.length <= MAX_LOGO_DATA_URL_CHARS) return url;
  }

  throw new Error('Imaginea este prea complexă. Încearcă un logo mai simplu.');
}
